import { useSearchParams } from 'react-router-dom';
import { useCallback, useMemo } from 'react';
import { statusReverseMatching } from '../../../pages/Incidents/utils/statusMatching';
import moment from 'moment';

const responseTimeMatching = (value) => {
  switch (value) {
    case '<5 Min':
      return { trt_sign: '<', trt_value: '300' };

    case '10+ Min':
      return { trt_sign: '>', trt_value: '600' };

    case '5-10 Min':
      return { trt_sign: 'between', trt_value: '300|600' };
    default:
      break;
  }
};

export const FILTER_PARAMS = {
  from_date: 'fromDate',
  to_date: 'toDate',
  incident_type: 'incidentType',
  status: 'status',
  neris_status: 'nerisStatus',
  response_time: 'responseTime',
  department: 'department',
};

export const getFilterParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const params = {};

  const fromDate = urlParams.get(FILTER_PARAMS.from_date);
  const toDate = urlParams.get(FILTER_PARAMS.to_date);

  if (fromDate && toDate) {
    params.start_dt = moment(fromDate).format('YYYY-MM-DD HH:mm:ss');
    params.end_dt = moment(toDate).format('YYYY-MM-DD HH:mm:ss');
  }

  const incidentType = urlParams.get(FILTER_PARAMS.incident_type);
  if (incidentType) {
    params.type = incidentType;
  }

  const status = urlParams.get(FILTER_PARAMS.status);
  if (status) {
    params.status = status.toLowerCase();
  }

  const nerisStatus = urlParams.get(FILTER_PARAMS.neris_status);
  if (nerisStatus) {
    params.nerisStatus = statusReverseMatching(nerisStatus);
  }

  const responseTime = urlParams.get(FILTER_PARAMS.response_time);
  if (responseTime) {
    params.responseTime = responseTimeMatching(responseTime);
  }

  const department = urlParams.get(FILTER_PARAMS.department);
  if (department) {
    params.department = department;
  }

  return params;
};

export const deleteFilterParamsFromUrl = (prevParams) => {
  Object.values(FILTER_PARAMS).forEach((param) => prevParams.delete(param));
};

export const initSettings = {
  dateRange: {},
  incidentType: [],
  status: [],
  nerisStatus: [],
  responseTime: [],
  department: [],
};

export const useFiltersUrlState = () => {
  const [params, setParams] = useSearchParams();

  // Get filters from URL
  const filters = useMemo(() => {
    const urlFilters = { ...initSettings };

    // Handle dateRange (fromDate and toDate)
    const fromDate = params.get(FILTER_PARAMS.from_date);
    const toDate = params.get(FILTER_PARAMS.to_date);
    if (fromDate && toDate) {
      urlFilters.dateRange = { fromDate, toDate };
    }

    // Handle array filters
    const incidentType = params.get(FILTER_PARAMS.incident_type);
    if (incidentType) {
      urlFilters.incidentType = incidentType.split(',');
    }

    const status = params.get(FILTER_PARAMS.status);
    if (status) {
      urlFilters.status = [status];
    }

    const nerisStatus = params.get(FILTER_PARAMS.neris_status);
    if (nerisStatus) {
      urlFilters.nerisStatus = [nerisStatus];
    }

    const responseTime = params.get(FILTER_PARAMS.response_time);
    if (responseTime) {
      urlFilters.responseTime = [responseTime];
    }

    const department = params.get(FILTER_PARAMS.department);
    if (department) {
      urlFilters.department = [department];
    }

    return urlFilters;
  }, [params]);

  // Set filters in URL
  const setFilters = useCallback(
    (newFilters) => {
      setParams((prev) => {
        // Clear all filter-related params first
        deleteFilterParamsFromUrl(prev);

        // Set new filter params
        if (newFilters.dateRange?.fromDate && newFilters.dateRange?.toDate) {
          prev.set(FILTER_PARAMS.from_date, newFilters.dateRange.fromDate);
          prev.set(FILTER_PARAMS.to_date, newFilters.dateRange.toDate);
        }

        if (!!newFilters.incidentType?.length) {
          prev.set(
            FILTER_PARAMS.incident_type,
            newFilters.incidentType.join(',')
          );
        }

        if (!!newFilters.status?.length) {
          prev.set(FILTER_PARAMS.status, newFilters.status[0]);
        }

        if (!!newFilters.nerisStatus?.length) {
          prev.set(FILTER_PARAMS.neris_status, newFilters.nerisStatus[0]);
        }

        if (!!newFilters.responseTime?.length) {
          prev.set(FILTER_PARAMS.response_time, newFilters.responseTime[0]);
        }

        if (!!newFilters.department?.length) {
          prev.set(FILTER_PARAMS.department, newFilters.department[0]);
        }

        return prev;
      });
    },
    [setParams]
  );

  return {
    filters,
    setFilters,
    params,
    setParams,
  };
};
